import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SCALE_LABELS, SCALE10_LABELS, CATEGORY_ICONS, NUM_FORMATS } from "@/data/assessment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Check, Compass, Globe2, Cloud } from "lucide-react";
import { toast } from "sonner";

const ANALYZING_MESSAGES = [
  "Analyzing your lifestyle…", "Weighing your priorities…",
  "Comparing 31 global cities…", "Scoring climate, cost & safety…",
  "Ranking your best matches…",
];

function AnalyzingScreen() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % ANALYZING_MESSAGES.length), 1100);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="analyzing-screen" className="flex min-h-screen flex-col items-center justify-center bg-white px-6">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary">
        <Globe2 className="h-8 w-8 text-white animate-spin" style={{ animationDuration: "3s" }} />
      </div>
      <h2 className="mt-8 font-heading text-2xl font-semibold tracking-tight">{ANALYZING_MESSAGES[idx]}</h2>
      <p className="mt-2 text-sm text-slate-500">This usually takes a few seconds.</p>
      <div className="mt-8 h-1 w-64 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
      </div>
    </div>
  );
}

function condMatch(cond, answers) {
  if (!cond) return true;
  if (cond.any) return cond.any.some((c) => condMatch(c, answers));
  if (cond.all) return cond.all.every((c) => condMatch(c, answers));
  const val = answers[cond.key];
  if (cond.in) {
    if (val === undefined || val === null) return false;
    return Array.isArray(val) ? val.some((v) => cond.in.includes(v)) : cond.in.includes(val);
  }
  if (cond.not_in) {
    if (val === undefined || val === null) return false;
    return Array.isArray(val) ? val.every((v) => !cond.not_in.includes(v)) : !cond.not_in.includes(val);
  }
  return true;
}

function stepVisible(s, answers) {
  return condMatch(s.show_if, answers);
}

export default function Assessment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [answers, setAnswers] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [ready, setReady] = useState(false);
  const [cities, setCities] = useState([]);
  const [resumed, setResumed] = useState(false);
  const saveTimer = useRef(null);

  const visibleSteps = useMemo(
    () => steps.filter((s) => stepVisible(s, answers)),
    [steps, answers]
  );
  const total = visibleSteps.length;
  const current = visibleSteps[step];

  // Load city list + dynamic questionnaire (resume if a draft exists).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [cityRes, qRes] = await Promise.all([
          api.get("/cities"),
          api.get("/assessments/questionnaire"),
        ]);
        if (!active) return;
        setCities(cityRes.data || []);
        const q = qRes.data || {};
        const qSteps = q.steps || [];
        setSteps(qSteps);
        if (q.answers && Object.keys(q.answers).length > 0) {
          setAnswers(q.answers);
          setResumed(true);
        }
        setStep(Math.min(q.current_step || 0, Math.max(qSteps.length - 1, 0)));
      } catch {
        if (active) toast.error("Couldn't load the assessment. Please refresh and try again.");
      }
      finally { if (active) setReady(true); }
    })();
    return () => { active = false; };
  }, []);

  // Debounced auto-save of draft.
  const saveDraft = useCallback((nextAnswers, nextStep) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/assessments/draft", { answers: nextAnswers, current_step: nextStep })
        .catch(() => {}); // best-effort autosave; the answers stay in local state
    }, 700);
  }, []);

  // Prune answers that became irrelevant after a branching answer changed,
  // and keep the step index within the (possibly shorter) visible list.
  useEffect(() => {
    if (!steps.length) return;
    const visKeys = new Set(steps.filter((s) => stepVisible(s, answers)).map((s) => s.key));
    let changed = false;
    const next = { ...answers };
    steps.forEach((s) => {
      if (s.show_if && !visKeys.has(s.key) && next[s.key] !== undefined) {
        delete next[s.key];
        changed = true;
      }
    });
    if (changed) {
      setAnswers(next);
      saveDraft(next, step);
    }
    // `step` is intentionally omitted: pruning must only re-run when answers or
    // the question set change, never on simple navigation (would spam autosaves).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, steps, saveDraft]);

  useEffect(() => {
    if (total > 0 && step > total - 1) setStep(total - 1);
  }, [total, step]);

  const setValue = (v) => {
    setAnswers((a) => {
      const next = { ...a, [current.key]: v };
      saveDraft(next, step);
      return next;
    });
  };

  const goStep = (s) => {
    setStep(s);
    saveDraft(answers, s);
  };

  const Icon = (current && CATEGORY_ICONS[current.category]) || Compass;
  const progress = total ? ((step + 1) / total) * 100 : 0;
  const raw = current ? answers[current.key] : undefined;

  const canProceed = (() => {
    if (!current) return false;
    if (current.optional) return true;
    if (current.type === "scale" || current.type === "scale10" || current.type === "number") return true;
    if (current.type === "multiselect" || current.type === "rank") return Array.isArray(raw) && raw.length >= (current.min || 1);
    return raw !== undefined && raw !== null && String(raw).trim() !== "";
  })();

  const toggleMulti = (val) => {
    setAnswers((a) => {
      const list = Array.isArray(a[current.key]) ? a[current.key] : [];
      let next;
      if (val === "anywhere") {
        next = list.includes("anywhere") ? [] : ["anywhere"];
      } else {
        const base = list.filter((x) => x !== "anywhere");
        next = base.includes(val) ? base.filter((x) => x !== val) : [...base, val];
      }
      const merged = { ...a, [current.key]: next };
      saveDraft(merged, step);
      return merged;
    });
  };

  const toggleRank = (val) => {
    setAnswers((a) => {
      const list = Array.isArray(a[current.key]) ? a[current.key] : [];
      let next;
      if (list.includes(val)) next = list.filter((x) => x !== val);
      else if (list.length < 4) next = [...list, val];
      else next = list;
      const merged = { ...a, [current.key]: next };
      saveDraft(merged, step);
      return merged;
    });
  };

  const next = () => {
    if ((current.type === "scale" || current.type === "scale10" || current.type === "number") && answers[current.key] === undefined) {
      setValue(current.default);
    }
    if (step < total - 1) goStep(step + 1);
    else submit();
  };

  const back = () => (step > 0 ? goStep(step - 1) : navigate("/dashboard"));

  const submit = async () => {
    setAnalyzing(true);
    const vis = steps.filter((s) => stepVisible(s, answers));
    const payload = {};
    vis.forEach((s) => {
      let v = answers[s.key];
      if ((s.type === "scale" || s.type === "scale10" || s.type === "number") && v === undefined) v = s.default;
      if (v !== undefined) payload[s.key] = v;
    });
    try {
      const { data } = await api.post("/assessments", {
        answers: payload,
        question_ids: vis.map((s) => s.question_id),
      });
      navigate(`/results/${data.report_id}`);
    } catch {
      setAnalyzing(false);
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (analyzing) return <AnalyzingScreen />;
  if (!ready || !current) return <div className="flex min-h-screen items-center justify-center"><Compass className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const scaleVal = answers[current.key] ?? current.default ?? 3;
  const scale10Val = answers[current.key] ?? current.default ?? 5;
  const numVal = answers[current.key] ?? current.default;
  const numFormat = current.format ? NUM_FORMATS[current.format] : null;

  return (
    <div className="min-h-screen bg-white">
      <div className="fixed top-0 inset-x-0 z-20">
        <div className="h-1 bg-slate-100">
          <div className="h-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} data-testid="assessment-progress" />
        </div>
      </div>

      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-20">
        <div className="mb-6 flex items-center justify-between text-sm text-slate-500">
          <span className="flex items-center gap-2"><Compass className="h-4 w-4" /> {current.category || "Assessment"}</span>
          <span data-testid="step-counter">{step + 1} of {total}</span>
        </div>

        {resumed && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="resume-banner">
            <Cloud className="h-3.5 w-3.5" /> Resumed from your saved progress — answers auto-save as you go.
          </div>
        )}

        <div key={step} className="animate-fade-up">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <h1 className="mt-5 font-heading text-3xl font-semibold tracking-tight">{current.title}</h1>
          <p className="mt-2 text-slate-500">{current.subtitle}</p>

          <div className="mt-8">
            {current.type === "text" && (
              <Input autoFocus data-testid="assessment-input" className="h-12 text-base" placeholder={current.placeholder}
                value={raw || ""} onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && canProceed && next()} />
            )}

            {current.type === "textarea" && (
              <Textarea data-testid="assessment-textarea" className="min-h-[120px] text-base" placeholder={current.placeholder}
                value={raw || ""} onChange={(e) => setValue(e.target.value)} />
            )}

            {current.type === "select" && (
              <div className="grid gap-2.5">
                {current.options.map((opt) => {
                  const sel = raw === opt.value;
                  return (
                    <button key={opt.value} data-testid={`option-${opt.value}`} onClick={() => setValue(opt.value)}
                      className={`flex items-center justify-between rounded-lg border px-4 py-3.5 text-left text-sm font-medium transition-all duration-200 ${sel ? "border-slate-900 bg-primary text-white" : "border-slate-200 hover:border-slate-400"}`}>
                      {opt.label}{sel && <Check className="h-4 w-4" />}
                    </button>
                  );
                })}
              </div>
            )}

            {current.type === "multiselect" && (
              <div className="flex flex-wrap gap-2">
                {current.options.map((opt) => {
                  const list = Array.isArray(raw) ? raw : [];
                  const sel = list.includes(opt.value);
                  return (
                    <button key={opt.value} data-testid={`option-${opt.value}`} onClick={() => toggleMulti(opt.value)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${sel ? "border-slate-900 bg-primary text-white" : "border-slate-200 hover:border-slate-400"}`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}

            {current.type === "rank" && (
              <div className="flex flex-wrap gap-2">
                {current.options.map((opt) => {
                  const list = Array.isArray(raw) ? raw : [];
                  const rank = list.indexOf(opt.value);
                  const sel = rank >= 0;
                  return (
                    <button key={opt.value} data-testid={`option-${opt.value}`} onClick={() => toggleRank(opt.value)}
                      className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${sel ? "border-slate-900 bg-primary text-white" : "border-slate-200 hover:border-slate-400"}`}>
                      {sel && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-900">{rank + 1}</span>}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}

            {current.type === "citytags" && (
              <div className="flex flex-wrap gap-2 max-h-[320px] overflow-y-auto pr-1">
                {cities.map((c) => {
                  const list = Array.isArray(raw) ? raw : [];
                  const sel = list.includes(c.name);
                  return (
                    <button key={c.name} data-testid={`city-${c.name}`} onClick={() => toggleMulti(c.name)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-all duration-200 ${sel ? "border-slate-900 bg-primary text-white" : "border-slate-200 hover:border-slate-400"}`}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
            )}

            {current.type === "scale" && (
              <div>
                <div className="mb-6 text-center font-heading text-2xl font-semibold" data-testid="slider-value">
                  {SCALE_LABELS[scaleVal - 1]}
                </div>
                <Slider data-testid="assessment-slider" min={1} max={5} step={1} value={[scaleVal]} onValueChange={(v) => setValue(v[0])} />
                <div className="mt-2 flex justify-between text-xs text-slate-400"><span>Not at all</span><span>Essential</span></div>
              </div>
            )}

            {current.type === "scale10" && (
              <div>
                <div className="mb-6 text-center font-heading text-3xl font-semibold" data-testid="slider-value">
                  {scale10Val}<span className="text-lg text-slate-400"> / 10</span>
                </div>
                <Slider data-testid="assessment-slider" min={1} max={10} step={1} value={[scale10Val]} onValueChange={(v) => setValue(v[0])} />
                <div className="mt-2 flex justify-between text-xs text-slate-400"><span>{SCALE10_LABELS.min}</span><span>{SCALE10_LABELS.max}</span></div>
              </div>
            )}

            {current.type === "number" && (
              <div>
                <div className="mb-6 text-center font-heading text-3xl font-semibold" data-testid="slider-value">
                  {numFormat ? numFormat(numVal) : numVal}
                </div>
                <Slider data-testid="assessment-slider" min={current.min} max={current.max} step={current.step} value={[numVal]} onValueChange={(v) => setValue(v[0])} />
                <div className="mt-2 flex justify-between text-xs text-slate-400">
                  <span>{numFormat ? numFormat(current.min) : current.min}</span>
                  <span>{numFormat ? numFormat(current.max) : current.max}</span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-10 flex items-center justify-between">
            <Button variant="ghost" onClick={back} data-testid="assessment-back" className="gap-2 text-slate-500">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <Button onClick={next} disabled={!canProceed} data-testid="assessment-next" className="h-11 gap-2 px-6 font-medium">
              {step === total - 1 ? "See my matches" : "Continue"}<ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
